import React, { useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { endpoints } from '../../utils/api';

function clientName(matter) {
  return [matter?.client?.lastName, matter?.client?.firstName].filter(Boolean).join(', ');
}
function label(matter) {
  if (!matter) return '';
  return [clientName(matter), matter.title, matter.caseNumber].filter(Boolean).join(' — ');
}

export default function TaskCasePicker({ onChange }) {
  const [selected, setSelected] = useState(null), [query, setQuery] = useState('');
  const [results, setResults] = useState([]), [page, setPage] = useState(1), [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false), [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);setError('');
    const timer = setTimeout(async () => {
      try {
        const data = await endpoints.cases.list({ search: query.trim(), page, limit: 20 });
        if (cancelled) return;
        const incoming = Array.isArray(data.cases) ? data.cases : [];
        setResults(previous => page === 1 ? incoming : [...previous, ...incoming.filter(row => !previous.some(existing => existing.id === row.id))]);
        setPages(data.pagination?.pages || 1);
      } catch (failure) { if (!cancelled) setError('Could not search cases. Try typing again. You can also leave this task unlinked.'); }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true;clearTimeout(timer); };
  }, [query, page]);
  function clear() { setSelected(null);setQuery('');setPage(1);if(query || page !== 1)setResults([]);onChange(''); }
  return <Combobox value={selected} by="id" nullable onChange={matter => { setSelected(matter);onChange(matter?.id || ''); }}>
    {({ open }) => <div>
      <Combobox.Label className="block text-sm font-medium">Case (optional)</Combobox.Label>
      <div className="mt-1 flex gap-2">
        <Combobox.Input className="block min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" displayValue={label} placeholder="Search by client, case title or number…" autoComplete="off" onChange={event => {
          setQuery(event.target.value);setPage(1);setResults([]);setSelected(null);onChange('');
        }} />
        <Combobox.Button className="rounded-md border px-3 text-sm" aria-label="Browse cases">⌄</Combobox.Button>
        {selected && <button type="button" onClick={clear} className="rounded-md border px-3 text-sm" aria-label="Remove selected case">Clear</button>}
      </div>
      {open && <div className="mt-2 rounded-md border border-gray-200 bg-white shadow-sm">
        {loading && <p role="status" className="px-3 py-2 text-sm text-gray-500">Searching cases…</p>}
        {error && <p role="alert" className="px-3 py-2 text-sm text-red-700">{error}</p>}
        {!loading && !error && results.length === 0 && <p role="status" className="px-3 py-2 text-sm text-gray-500">No matching cases. Try a client name, title or case number.</p>}
        <Combobox.Options static modal={false} className="max-h-44 overflow-y-auto">
          {results.map(matter => <Combobox.Option key={matter.id} value={matter} className={({ active }) => `cursor-pointer px-3 py-2 text-sm ${active ? 'bg-blue-50' : ''}`}>
            <div className="font-medium text-gray-900">{clientName(matter) || matter.title || 'Untitled case'}</div>
            <div className="break-words text-xs text-gray-500">{[clientName(matter) && matter.title, matter.caseNumber && `Case ${matter.caseNumber}`].filter(Boolean).join(' · ')}</div>
          </Combobox.Option>)}
        </Combobox.Options>
        {!loading && !error && page < pages && <button type="button" className="w-full border-t px-3 py-2 text-sm text-blue-700" onMouseDown={event => event.preventDefault()} onClick={() => setPage(previous => previous + 1)}>Load more cases</button>}
      </div>}
      <p className="mt-1 text-xs text-gray-500">{selected ? 'This task will be linked to the selected case.' : 'Optional — search and select a case, or leave it unlinked.'}</p>
    </div>}
  </Combobox>;
}
